import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouterLinkWithHref } from '@angular/router';
import { RedirectToLoginGuard } from './modules/authentication/guards/redirect-to-login.guard';
import { IsAuthenticatedGuard } from './modules/authentication/guards/is-authenticated.guard';
import { CanAccessPageGuard } from './modules/authentication/guards/can-access-page.guard';
import { QuicklinkStrategy } from 'ngx-quicklink';

import { Config } from './config';

import { ProfileInsightPageResolver } from './services/pageloadresolver.service';

// Pages
import { LoginComponent } from './pages/login/login.component';
import { SingleSignOnComponent } from './pages/sso/single-sign-on.component';
import { FleetActivationComponent } from './pages/fleet-activation/fleet-activation.component';
import { CognitoSsoComponent } from './pages/cognito-sso/cognito-sso.component';
import { SupervisionSsoComponent } from './pages/supervision-sso/supervision-sso.component';

// Reports
import { CommonReportsComponent } from './pages/common-reports/common-reports.component';

// Login Logout
import { LoginWithV2tokenComponent } from './pages/login-with-v2token/login-with-v2token.component';
import { DriverImportComponent } from './pages/driver-import/driver-import.component';

const routes: Routes = [
  {
    path: '#/',
    redirectTo: Config.defaultPage,
  },

  // VRM Analytics Dashboard
  {
    path: 'vrm-analytics-dashboard',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () =>
      import('./pages/vrm-analytics-dashboard-container/vrm-analytics-dashboard.module').then(
        (m) => m.VrmAnalyticsDashboardModule
      ),
  },

  {
    path: 'vrm-analytics-dashboard/:group',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () =>
      import('./pages/vrm-analytics-dashboard-container/vrm-analytics-dashboard.module').then(
        (m) => m.VrmAnalyticsDashboardModule
      ),
  },

  // Profile Insights
  {
    path: 'profile-insights',
    resolve: { resolvedResponse: ProfileInsightPageResolver },
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () => import('./pages/profile-insights/profile-insights.module').then((m) => m.ProfileInsightsModule),
  },

  // ---- COMMON REPORTS ----
  {
    path: 'commonreports/:report',
    component: CommonReportsComponent,
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
  },

  // ---- SELF provisioning ----
  {
    path: 'self-provisioning',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () =>
      import('./pages/self-provisioning/self-provisioning.module').then((m) => m.SelfProvisioningModule),
  },

  // ---- FLEET REPORT DVCR ----
  {
    path: 'fleet-report-dvcr',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () => import('./pages/fleet-report-dvcr/fleet-dvcr.module').then((m) => m.FleetDvcrModule),
  },
  {
    path: 'trip-session-report',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () => import('./pages/trip-session-report/trip-session.module').then((m) => m.TripSessionModule),
  },
  {
    path: 'fleet-damages-report',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () => import('./pages/fleet-report-damages/fleet-damages.module').then((m) => m.FleetDamagesModule),
  },
  {
    path: 'damages-report',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () => import('./pages/fleet-report-damages/fleet-damages.module').then((m) => m.FleetDamagesModule),
  },
  // ---- Login Logout Report ----
  {
    path: 'mentor-login-report',
    canActivate: [RedirectToLoginGuard, CanAccessPageGuard],
    loadChildren: () =>
      import('./pages/login-logout-session-report/mentor-login-session.module').then((m) => m.MentorLoginSessionModule),
  },

  // GUIDES / FAQ
  {
    path: 'company-guide',
    canActivate: [RedirectToLoginGuard],
    loadChildren: () => import('./pages/company-guide/company-guide.module').then((m) => m.CompanyGuideModule),
  },
  {
    path: 'vrm-guide',
    canActivate: [RedirectToLoginGuard],
    loadChildren: () => import('./pages/vrm-guide/vrm-guide.module').then((m) => m.VrmGuideModule),
  },

  // ---- AUTHENTICATION ----
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [IsAuthenticatedGuard],
  },
  {
    path: 'login/:namespace',
    component: LoginComponent,
    canActivate: [IsAuthenticatedGuard],
  },
  {
    path: 'login_with_company',
    component: LoginComponent,
    canActivate: [IsAuthenticatedGuard],
  },
  {
    path: 'login_with_company/:namespace',
    component: LoginComponent,
    canActivate: [IsAuthenticatedGuard],
  },
  {
    path: 'sso/:token',
    component: SingleSignOnComponent,
  },
  {
    path: 'cognito-sso/:authCode',
    component: CognitoSsoComponent,
  },
  {
    path: 'sv-sso/:uuid',
    component: SupervisionSsoComponent,
  },
  {
    path: 'login_with_v2token',
    component: LoginWithV2tokenComponent,
  },

  {
    path: 'security',
    canActivate: [RedirectToLoginGuard],
    loadChildren: () => import('./pages/security/security.module').then((m) => m.SecurityModule),
  },
  {
    path: 'reset-password/:token',
    canActivate: [IsAuthenticatedGuard],
    loadChildren: () => import('./pages/password-reset/password-reset.module').then((m) => m.PasswordResetModule),
  },

  // Create Password - Fleet Activation
  {
    path: 'fleet_activation/:token',
    component: FleetActivationComponent,
  },

  // Resource preview
  {
    path: 'resource-preview',
    canActivate: [RedirectToLoginGuard],
    loadChildren: () => import('./pages/resource-preview/resource-preview.module').then((m) => m.ResourcePreviewModule),
  },
  //Driver Import
  {
    path: 'driver-import',
    component: DriverImportComponent,
    canActivate: [RedirectToLoginGuard],
  },

  // Routes not found - must come last
  {
    path: '**',
    redirectTo: Config.defaultPage,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      preloadingStrategy: QuicklinkStrategy,
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
  providers: [RouterLinkWithHref],
})
export class AppRoutingModule {}
